import React, { useState, useEffect } from 'react';
import './ConfigModal.css';
import { apiGet, apiPut } from '../utils/api';

const ConfigModal = ({ onClose, onSuccess }) => {
  const [config, setConfig] = useState({
    animationTheme: 'original',
    horarios: {
      lunesViernes: {
        inicio: '08:00',
        fin: '18:00',
        habilitado: true
      },
      sabados: {
        inicio: '09:00',
        fin: '14:00',
        habilitado: true
      },
      domingos: {
        inicio: '10:00',
        fin: '12:00',
        habilitado: false
      }
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [configLoaded, setConfigLoaded] = useState(false);
  const [noConfigMessage, setNoConfigMessage] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      setNoConfigMessage('');
      
      // Token se maneja automáticamente con cookies
      const response = await apiGet('/api/config');

      if (response.ok) {
        const data = await response.json();
    
        
        // Verificar si hay configuración válida
        if (data.config) {
          setConfig({
            animationTheme: data.config.animationTheme || 'original',
            horarios: data.config.horarios || {
              lunesViernes: { inicio: '08:00', fin: '18:00', habilitado: true },
              sabados: { inicio: '09:00', fin: '14:00', habilitado: true },
              domingos: { inicio: '10:00', fin: '12:00', habilitado: false }
            }
          });
          setConfigLoaded(true);
        } else {
          // No hay configuración en base de datos
          setNoConfigMessage('⚠️ No se encontró configuración en la base de datos. Se mostrarán valores por defecto.');
          setConfigLoaded(true);
        }
      } else {
        setError('Error al cargar la configuración desde el servidor');
        setConfigLoaded(true);
      }
    } catch (error) {
      setError('Error de conexión al cargar configuración');
      setConfigLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Manejar cambio de tema de animación
    if (name === 'animationTheme') {
      setConfig(prev => ({
        ...prev,
        animationTheme: value
      }));
      return;
    }
    
    const [periodo, campo] = name.split('.');
    setConfig(prev => ({
      ...prev,
      horarios: {
        ...prev.horarios,
        [periodo]: {
          ...prev.horarios[periodo],
          [campo]: type === 'checkbox' ? checked : value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Token se maneja automáticamente con cookies
      
      // Enviar configuración de tema y horarios
      const configToSend = {
        animationTheme: config.animationTheme,
        horarios: config.horarios
      };

      const response = await apiPut('/api/config', configToSend);

      const data = await response.json();

      if (response.ok) {
        setSuccess('Configuración actualizada exitosamente');
        
        // Llamar a onSuccess para disparar el evento de cambio de tema INMEDIATAMENTE
        if (onSuccess) {
          onSuccess();
        }
        
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(data.message || 'Error al actualizar configuración');
      }
    } catch (error) {
      setError('Error de conexión. Verifique que el servidor esté ejecutándose.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="config-modal-overlay">
      <div className="config-modal-content-container">
        <div className="config-modal-header">
          <h2>⚙️ Configuración del Sistema</h2>
          <button className="config-modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="config-modal-scrollable-content">
          <form onSubmit={handleSubmit} className="config-modal-form">
            {loading && (
              <div className="config-modal-loading-message">
                🔄 Cargando configuración desde base de datos...
              </div>
            )}
            
            {noConfigMessage && (
              <div className="config-modal-no-config-message">
                {noConfigMessage}
              </div>
            )}
            
            {/* Selector de Tema de Animación */}
            <div className="config-modal-section">
              <h3>🎨 Tema de Animación</h3>
              <div className="config-modal-theme-selector">
                <label htmlFor="animationTheme">Elige el tema visual del sistema:</label>
                <select 
                  id="animationTheme"
                  name="animationTheme"
                  value={config.animationTheme}
                  onChange={handleChange}
                  className="config-modal-select"
                >
                  <option value="original">Original (Glass Neural)</option>
                  <option value="dia-muertos">Día de Muertos 🏵️</option>
                  <option value="navidad">Navidad 🎄</option>
                  <option value="independencia-mexico">Independencia de México 🇲🇽</option>
                </select>
                <p className="config-modal-help-text">
                  Este tema afecta las animaciones de fondo del sistema
                </p>
              </div>
            </div>

            <div className="config-modal-section">
              <h3>🕐 Horarios de Apertura</h3>
              
              <div className="config-modal-horario-group">
                <h4>Lunes - Viernes</h4>
                <div className="config-modal-horario-controls">
                  <label className="config-modal-checkbox-label">
                    <input
                      type="checkbox"
                      name="lunesViernes.habilitado"
                      checked={config.horarios.lunesViernes.habilitado}
                      onChange={handleChange}
                    />
                    <span>Habilitado</span>
                  </label>
                  <div className="config-modal-time-inputs">
                    <input
                      type="time"
                      name="lunesViernes.inicio"
                      value={config.horarios.lunesViernes.inicio}
                      onChange={handleChange}
                      disabled={!config.horarios.lunesViernes.habilitado}
                    />
                    <span>a</span>
                    <input
                      type="time"
                      name="lunesViernes.fin"
                      value={config.horarios.lunesViernes.fin}
                      onChange={handleChange}
                      disabled={!config.horarios.lunesViernes.habilitado}
                    />
                  </div>
                </div>
              </div>

              <div className="config-modal-horario-group">
                <h4>Sábados</h4>
                <div className="config-modal-horario-controls">
                  <label className="config-modal-checkbox-label">
                    <input
                      type="checkbox"
                      name="sabados.habilitado"
                      checked={config.horarios.sabados.habilitado}
                      onChange={handleChange}
                    />
                    <span>Habilitado</span>
                  </label>
                  <div className="config-modal-time-inputs">
                    <input
                      type="time"
                      name="sabados.inicio"
                      value={config.horarios.sabados.inicio}
                      onChange={handleChange}
                      disabled={!config.horarios.sabados.habilitado}
                    />
                    <span>a</span>
                    <input
                      type="time"
                      name="sabados.fin"
                      value={config.horarios.sabados.fin}
                      onChange={handleChange}
                      disabled={!config.horarios.sabados.habilitado}
                    />
                  </div>
                </div>
              </div>

              <div className="config-modal-horario-group">
                <h4>Domingos</h4>
                <div className="config-modal-horario-controls">
                  <label className="config-modal-checkbox-label">
                    <input
                      type="checkbox"
                      name="domingos.habilitado"
                      checked={config.horarios.domingos.habilitado}
                      onChange={handleChange}
                    />
                    <span>Habilitado</span>
                  </label>
                  <div className="config-modal-time-inputs">
                    <input
                      type="time"
                      name="domingos.inicio"
                      value={config.horarios.domingos.inicio}
                      onChange={handleChange}
                      disabled={!config.horarios.domingos.habilitado}
                    />
                    <span>a</span>
                    <input
                      type="time"
                      name="domingos.fin"
                      value={config.horarios.domingos.fin}
                      onChange={handleChange}
                      disabled={!config.horarios.domingos.habilitado}
                    />
                  </div>
                </div>
              </div>
            </div>

            {error && <div className="config-modal-error-message">{error}</div>}
            {success && <div className="config-modal-success-message">{success}</div>}
          </form>
        </div>

        <div className="config-modal-footer">
          <button 
            type="submit" 
            className="config-modal-save-button"
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
          <button 
            type="button" 
            className="config-modal-cancel-button"
            onClick={onClose}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigModal;
